<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Community-Based Health Insurance</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Keep your finances and membership up to date" name="description" />
    <meta content="Kelvin Njuguna Ndungu" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />

    <!-- Bootstrap select styling -->
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">

    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Edit Hospital</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Hospitals</a></li>
                    <li class="breadcrumb-item active">Edit Hospital</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div class="row justify-content-center align-items-center d-flex">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="btn-toolbar p-3 d-flex flex-column w-100" role="toolbar">
                    <h4 class="card-title text-uppercase mb-1">
                      Edit Hospital Information
                    </h4>
                    <p class="pb-0 mb-0">Update the hospital details and services offered. Changes will be reflected immediately in the CBHI system.</p>
                  </div>
                </div>

                <!-- Alert Messages -->
                <div class="card-body">
                  <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-check-all me-2"></i>
                    {{ message }}
                    <button type="button" class="btn-close" @click="clearAlerts"></button>
                  </div>

                  <div v-if="alert.info" class="alert alert-info alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-information me-2"></i>
                    {{ message }}
                    <button type="button" class="btn-close" @click="clearAlerts"></button>
                  </div>

                  <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-alert-outline me-2"></i>
                    {{ message }}
                    <button type="button" class="btn-close" @click="clearAlerts"></button>
                  </div>

                  <div class="row">
                    <!-- Basic Hospital Information -->
                    <div class="col-12">
                      <div class="bg-primary border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-hospital-building me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Hospital Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 col-lg-6 mb-3">
                      <div class="mb-3">
                        <label for="hospital-name" class="form-label">Hospital Name <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.hospitalName" type="text" class="form-control" id="hospital-name" placeholder="Enter Hospital Name" spellcheck="false" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 col-lg-6 mb-3">
                      <div class="mb-3">
                        <label for="hospital-license" class="form-label">License Number <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.licenseNumber" type="text" class="form-control" id="hospital-license" placeholder="Enter Hospital License Number" spellcheck="false" required :disabled="true">
                        <small class="text-muted">License number cannot be changed</small>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                      <div class="mb-3">
                        <label for="hospital-category" class="form-label">Hospital Category <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.categoryId" class="form-select" id="hospital-category" required>
                          <option value="">Select Hospital Category</option>
                          <option v-for="category in hospitalCategories" :key="category.category_id" :value="category.category_id">
                            {{ category.category_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                      <div class="mb-3">
                        <label for="ownership-type" class="form-label">Ownership Type <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.ownershipId" class="form-select" id="ownership-type" required>
                          <option value="">Select Ownership</option>
                          <option v-for="ownership in ownershipTypes" :key="ownership.ownership_id" :value="ownership.ownership_id">
                            {{ ownership.ownership_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                      <div class="mb-3">
                        <label for="bed-capacity" class="form-label">Bed Capacity</label>
                        <input v-model.number="hospitalForm.bedCapacity" type="number" class="form-control" id="bed-capacity" placeholder="Number of Beds" min="0">
                      </div>
                    </div>

                    <!-- Location Information -->
                    <div class="col-12">
                      <div class="bg-info border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-map-marker me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Location Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="region-input" class="form-label">Region <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.regionId" @change="loadZones" id="region-input" class="form-select" required>
                          <option value="">Select Region</option>
                          <option v-for="region in regions" :key="region.region_id" :value="region.region_id">
                            {{ region.region_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="zone-input" class="form-label">Zone <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.zoneId" @change="loadWoredas" id="zone-input" class="form-select" required :disabled="!hospitalForm.regionId">
                          <option value="">Select Zone</option>
                          <option v-for="zone in zones" :key="zone.zone_id" :value="zone.zone_id">
                            {{ zone.zone_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="woreda-input" class="form-label">Woreda <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.woredaId" @change="loadKebeles" id="woreda-input" class="form-select" required :disabled="!hospitalForm.zoneId">
                          <option value="">Select Woreda</option>
                          <option v-for="woreda in woredas" :key="woreda.woreda_id" :value="woreda.woreda_id">
                            {{ woreda.woreda_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="kebele-input" class="form-label">Kebele</label>
                        <select v-model="hospitalForm.kebeleId" id="kebele-input" class="form-select" :disabled="!hospitalForm.woredaId">
                          <option value="">Select Kebele</option>
                          <option v-for="kebele in kebeles" :key="kebele.kebele_id" :value="kebele.kebele_id">
                            {{ kebele.kebele_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 mb-3">
                      <div class="mb-3">
                        <label for="address-input" class="form-label">Physical Address <strong class="text-danger fw-bold">*</strong></label>
                        <textarea v-model="hospitalForm.physicalAddress" class="form-control" id="address-input" rows="2" placeholder="Enter detailed physical address" required></textarea>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 mb-3">
                      <div class="mb-3">
                        <label for="postal-address" class="form-label">Postal Address</label>
                        <textarea v-model="hospitalForm.postalAddress" class="form-control" id="postal-address" rows="2" placeholder="P.O. Box, City, Region"></textarea>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="postal-code" class="form-label">Postal Code</label>
                        <input v-model="hospitalForm.postalCode" type="text" class="form-control" id="postal-code" placeholder="e.g., 1000">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="po-box" class="form-label">P.O. Box</label>
                        <input v-model="hospitalForm.poBox" type="text" class="form-control" id="po-box" placeholder="e.g., 12345">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="latitude" class="form-label">Latitude</label>
                        <input v-model.number="hospitalForm.latitude" type="number" step="any" class="form-control" id="latitude" placeholder="e.g., 9.0307">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="longitude" class="form-label">Longitude</label>
                        <input v-model.number="hospitalForm.longitude" type="number" step="any" class="form-control" id="longitude" placeholder="e.g., 38.7616">
                      </div>
                    </div>

                    <!-- Contact Information -->
                    <div class="col-12">
                      <div class="bg-success border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-phone me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Contact Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="phone-primary" class="form-label">Primary Phone <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.primaryPhone" type="tel" class="form-control" id="phone-primary" placeholder="+251 911 123456" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="phone-secondary" class="form-label">Secondary Phone</label>
                        <input v-model="hospitalForm.secondaryPhone" type="tel" class="form-control" id="phone-secondary" placeholder="+251 911 654321">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="email-hospital" class="form-label">Hospital Email</label>
                        <input v-model="hospitalForm.hospitalEmail" type="email" class="form-control" id="email-hospital" placeholder="hospital@example.com">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="website" class="form-label">Website</label>
                        <input v-model="hospitalForm.website" type="url" class="form-control" id="website" placeholder="https://hospital.com">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="contact-person" class="form-label">Contact Person <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.contactPerson" type="text" class="form-control" id="contact-person" placeholder="Dr. John Doe" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="contact-position" class="form-label">Contact Person Position</label>
                        <input v-model="hospitalForm.contactPersonPosition" type="text" class="form-control" id="contact-position" placeholder="Medical Director">
                      </div>
                    </div>

                    <!-- Medical Services -->
                    <div class="col-12">
                      <div class="bg-warning border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-medical-bag me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Medical Services Offered</p>
                      </div>
                    </div>

                    <div class="col-12 mb-3">
                      <div class="row">
                        <!-- General Services -->
                        <div class="col-md-6">
                          <h6 class="mb-3">General Services</h6>
                          <div v-for="service in generalServices" :key="service.service_id" class="form-check mb-2">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :id="`service-${service.service_id}`"
                                :value="service.service_id"
                                v-model="hospitalForm.services"
                            >
                            <label class="form-check-label" :for="`service-${service.service_id}`">
                              {{ service.service_name }}
                            </label>
                          </div>
                        </div>

                        <!-- Specialized Services -->
                        <div class="col-md-6">
                          <h6 class="mb-3">Specialized Services</h6>
                          <div v-for="service in specializedServices" :key="service.service_id" class="form-check mb-2">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                :id="`service-${service.service_id}`"
                                :value="service.service_id"
                                v-model="hospitalForm.services"
                            >
                            <label class="form-check-label" :for="`service-${service.service_id}`">
                              {{ service.service_name }}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Banking Information Section -->
                    <div class="col-12">
                      <div class="bg-info border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-bank me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Banking Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="bank-name" class="form-label">Bank Name <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.bankName" type="text" class="form-control" id="bank-name" placeholder="Enter Bank Name" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="bank-account" class="form-label">Bank Account Number <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.bankAccountNumber" type="text" class="form-control" id="bank-account" placeholder="Enter Account Number" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="account-holder" class="form-label">Account Holder Name <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.accountHolderName" type="text" class="form-control" id="account-holder" placeholder="Account Holder Name" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="branch-name" class="form-label">Branch Name</label>
                        <input v-model="hospitalForm.bankBranchName" type="text" class="form-control" id="branch-name" placeholder="Bank Branch Name">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="branch-code" class="form-label">Branch Code</label>
                        <input v-model="hospitalForm.bankBranchCode" type="text" class="form-control" id="branch-code" placeholder="Branch Code">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="swift-code" class="form-label">SWIFT/BIC Code</label>
                        <input v-model="hospitalForm.swiftBicCode" type="text" class="form-control" id="swift-code" placeholder="For international transfers">
                      </div>
                    </div>

                    <!-- Additional Information -->
                    <div class="col-12">
                      <div class="bg-secondary border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-information me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Additional Information</p>
                      </div>
                    </div>

                    <div class="col-12 mb-3">
                      <div class="mb-3">
                        <label for="operating-hours" class="form-label">Operating Hours</label>
                        <textarea v-model="hospitalForm.operatingHours" class="form-control" id="operating-hours" rows="2" placeholder="e.g., Monday-Friday: 8:00 AM - 6:00 PM, Saturday: 8:00 AM - 2:00 PM"></textarea>
                      </div>
                    </div>

                    <div class="col-12 mb-3">
                      <div class="mb-3">
                        <label for="special-notes" class="form-label">Special Notes/Comments</label>
                        <textarea v-model="hospitalForm.specialNotes" class="form-control" id="special-notes" rows="3" placeholder="Any additional information about the hospital"></textarea>
                      </div>
                    </div>

                    <div class="col-12">
                      <div class="form-check mb-3">
                        <input v-model="hospitalForm.emergencyServices" class="form-check-input" type="checkbox" id="emergency-services">
                        <label class="form-check-label" for="emergency-services">24/7 Emergency Services Available</label>
                      </div>
                      <div class="form-check mb-3">
                        <input v-model="hospitalForm.referralCenter" class="form-check-input" type="checkbox" id="referral-center">
                        <label class="form-check-label" for="referral-center">Acts as Referral Center</label>
                      </div>
                      <div class="form-check mb-3">
                        <input v-model="hospitalForm.ambulanceAvailable" class="form-check-input" type="checkbox" id="ambulance-service">
                        <label class="form-check-label" for="ambulance-service">Ambulance Service Available</label>
                      </div>
                    </div>

                    <!-- Status Change Section -->
                    <div class="col-12">
                      <div class="bg-danger border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-shield-check me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Hospital Status</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 mb-3">
                      <div class="mb-3">
                        <label for="hospital-status" class="form-label">Status</label>
                        <select v-model="hospitalForm.status" class="form-select" id="hospital-status">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                        </select>
                        <small class="text-muted">Changing status will affect the hospital's ability to submit claims</small>
                      </div>
                    </div>

                    <!-- Status Reason Textbox - Shows when status is inactive or suspended -->
                    <div v-if="hospitalForm.status === 'inactive' || hospitalForm.status === 'suspended'" class="col-12 mb-3">
                      <div class="mb-3">
                        <label for="status-reason" class="form-label">
                          Reason for {{ hospitalForm.status === 'suspended' ? 'Suspension' : 'Inactivation' }}
                          <strong class="text-danger fw-bold">*</strong>
                        </label>
                        <textarea
                            v-model="hospitalForm.statusReason"
                            class="form-control"
                            id="status-reason"
                            rows="4"
                            :placeholder="`Please provide detailed reason for ${hospitalForm.status === 'suspended' ? 'suspending' : 'inactivating'} this hospital...`"
                            required
                        ></textarea>
                        <small class="text-muted">This reason will be recorded for audit purposes and included in notifications.</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-white border-top py-3">
                  <div class="col-12">
                    <button @click="updateHospital" type="button" class="btn btn-primary waves-effect waves-light text-uppercase" :disabled="loading">
                      <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      <i v-else class="mdi mdi-content-save font-size-16 align-middle me-2"></i>
                      {{ loading ? 'Updating...' : 'Update Hospital' }}
                    </button>
                    <button @click="cancelEdit" type="button" class="btn btn-outline-secondary waves-effect text-uppercase mx-3">
                      <i class="mdi mdi-close font-size-16 align-middle me-2"></i> Cancel
                    </button>
                    <button @click="viewChanges" type="button" class="btn btn-outline-info waves-effect text-uppercase">
                      <i class="mdi mdi-compare font-size-16 align-middle me-2"></i> View Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- end col -->
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Changes Modal -->
      <div class="modal fade" id="changesModal" tabindex="-1" role="dialog" aria-labelledby="changesModalTitle" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="changesModalTitle">
                <i class="mdi mdi-compare me-2"></i>
                Pending Changes
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div v-if="changes.length === 0" class="text-center py-4">
                <i class="mdi mdi-information display-4 text-muted"></i>
                <p class="mt-2 text-muted">No changes have been made yet.</p>
              </div>
              <div v-else>
                <table class="table table-bordered">
                  <thead>
                  <tr>
                    <th>Field</th>
                    <th>Original Value</th>
                    <th>New Value</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="change in changes" :key="change.field">
                    <td>{{ change.label }}</td>
                    <td>
                        <span v-if="change.field === 'emergencyServices' || change.field === 'referralCenter' || change.field === 'ambulanceAvailable'">
                          {{ change.original ? 'Yes' : 'No' }}
                        </span>
                      <span v-else>{{ change.original || 'N/A' }}</span>
                    </td>
                    <td>
                        <span v-if="change.field === 'emergencyServices' || change.field === 'referralCenter' || change.field === 'ambulanceAvailable'">
                          {{ change.new ? 'Yes' : 'No' }}
                        </span>
                      <span v-else>{{ change.new || 'N/A' }}</span>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "HospitalsEdit",
  components: { NavigationBar },
  data() {
    return {
      loading: false,
      hospitalId: null,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',

      // Original hospital data for comparison
      originalHospitalData: {},

      // Form data
      hospitalForm: {
        hospitalName: '',
        licenseNumber: '',
        categoryId: '',
        ownershipId: '',
        bedCapacity: null,
        regionId: '',
        zoneId: '',
        woredaId: '',
        kebeleId: '',
        physicalAddress: '',
        postalAddress: '',
        postalCode: '',
        poBox: '',
        latitude: null,
        longitude: null,
        primaryPhone: '',
        secondaryPhone: '',
        hospitalEmail: '',
        website: '',
        contactPerson: '',
        contactPersonPosition: '',
        bankName: '',
        bankAccountNumber: '',
        accountHolderName: '',
        bankBranchName: '',
        bankBranchCode: '',
        swiftBicCode: '',
        operatingHours: '',
        specialNotes: '',
        emergencyServices: false,
        referralCenter: false,
        ambulanceAvailable: false,
        services: [],
        status: 'active',
        statusReason: ''
      },

      // Dropdown data
      regions: [],
      zones: [],
      woredas: [],
      kebeles: [],
      hospitalCategories: [],
      ownershipTypes: [],
      services: [],
      generalServices: [],
      specializedServices: [],

      // Track changes
      changes: []
    };
  },

  mounted() {
    this.loadHospitalData();
    this.loadInitialData();
  },

  methods: {
    checkAuthentication() {
      const token = localStorage.getItem("authToken");
      if (!token) {
        router.push('/login');
      }
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      // Auto-hide success/info alerts after 5 seconds
      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    loadHospitalData() {
      // Get hospital ID from route params
      this.hospitalId = this.$route.params.id;

      // Get hospital data from sessionStorage
      const hospitalData = sessionStorage.getItem('editHospital');

      if (hospitalData) {
        const hospital = JSON.parse(hospitalData);

        // Store original data for comparison
        this.originalHospitalData = { ...hospital };

        // Map the data to form fields
        this.hospitalForm = {
          hospitalName: hospital.hospital_name || '',
          licenseNumber: hospital.license_number || '',
          categoryId: hospital.category_id || '',
          ownershipId: hospital.ownership_id || '',
          bedCapacity: hospital.bed_capacity || null,
          regionId: hospital.region_id || '',
          zoneId: hospital.zone_id || '',
          woredaId: hospital.woreda_id || '',
          kebeleId: hospital.kebele_id || '',
          physicalAddress: hospital.physical_address || '',
          postalAddress: hospital.postal_address || '',
          postalCode: hospital.postal_code || '',
          poBox: hospital.po_box || '',
          latitude: hospital.latitude || null,
          longitude: hospital.longitude || null,
          primaryPhone: hospital.primary_phone || '',
          secondaryPhone: hospital.secondary_phone || '',
          hospitalEmail: hospital.hospital_email || '',
          website: hospital.website || '',
          contactPerson: hospital.contact_person || '',
          contactPersonPosition: hospital.contact_person_position || '',
          bankName: hospital.bank_name || '',
          bankAccountNumber: hospital.bank_account_number || '',
          accountHolderName: hospital.account_holder_name || '',
          bankBranchName: hospital.bank_branch_name || '',
          bankBranchCode: hospital.bank_branch_code || '',
          swiftBicCode: hospital.swift_bic_code || '',
          operatingHours: hospital.operating_hours || '',
          specialNotes: '',
          emergencyServices: hospital.emergency_services == 1,
          referralCenter: hospital.referral_center == 1,
          ambulanceAvailable: hospital.ambulance_available == 1,
          services: [],
          status: hospital.status || 'active',
          statusReason: hospital.status_reason || ''
        };

        // Load hospital services
        this.loadHospitalServices();
      } else {
        this.showAlert('failed', 'Hospital data not found. Please select a hospital from the list.');
        setTimeout(() => {
          router.push('/hospitals');
        }, 2000);
      }
    },

    async loadHospitalServices() {
      try {
        const data = {
          function: "getHospitalServices",
          hospitalId: this.hospitalId
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          // Extract service IDs from the response
          const services = response.data.data.services || [];
          this.hospitalForm.services = services.map(s => s.service_id);
        }
      } catch (error) {
        console.error("Error loading hospital services:", error);
      }
    },

    async loadInitialData() {
      await Promise.all([
        this.loadRegions(),
        this.loadHospitalCategories(),
        this.loadOwnershipTypes(),
        this.loadServices()
      ]);

      // Load dependent dropdowns based on existing data
      if (this.hospitalForm.regionId) {
        await this.loadZones();
      }
      if (this.hospitalForm.zoneId) {
        await this.loadWoredas();
      }
      if (this.hospitalForm.woredaId) {
        await this.loadKebeles();
      }
    },

    async loadRegions() {
      try {
        const data = {
          function: "getRegions"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.regions = response.data.data.regions;
        }
      } catch (error) {
        console.error("Error loading regions:", error);
      }
    },

    async loadZones() {
      if (!this.hospitalForm.regionId) {
        this.zones = [];
        this.woredas = [];
        this.kebeles = [];
        return;
      }

      try {
        const data = {
          function: "getZones",
          regionId: this.hospitalForm.regionId
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.zones = response.data.data.zones;
        }
      } catch (error) {
        console.error("Error loading zones:", error);
      }
    },

    async loadWoredas() {
      if (!this.hospitalForm.zoneId) {
        this.woredas = [];
        this.kebeles = [];
        return;
      }

      try {
        const data = {
          function: "getWoredas",
          zoneId: this.hospitalForm.zoneId
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.woredas = response.data.data.woredas;
        }
      } catch (error) {
        console.error("Error loading woredas:", error);
      }
    },

    async loadKebeles() {
      if (!this.hospitalForm.woredaId) {
        this.kebeles = [];
        return;
      }

      try {
        const data = {
          function: "getKebeles",
          woredaId: this.hospitalForm.woredaId
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.kebeles = response.data.data.kebeles;
        }
      } catch (error) {
        console.error("Error loading kebeles:", error);
      }
    },

    async loadHospitalCategories() {
      try {
        const data = {
          function: "getHospitalCategories"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.hospitalCategories = response.data.data.categories;
        }
      } catch (error) {
        console.error("Error loading hospital categories:", error);
      }
    },

    async loadOwnershipTypes() {
      try {
        const data = {
          function: "getOwnershipTypes"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.ownershipTypes = response.data.data.ownershipTypes || [];
        }
      } catch (error) {
        console.error("Error loading ownership types:", error);
      }
    },

    async loadServices() {
      try {
        const data = {
          function: "getServices"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.services = response.data.data.services;
          // Separate services into general and specialized
          this.generalServices = this.services.filter(s => s.service_category === 'general');
          this.specializedServices = this.services.filter(s => s.service_category === 'specialized');
        }
      } catch (error) {
        console.error("Error loading services:", error);
      }
    },

    validateForm() {
      // Required fields validation
      const requiredFields = [
        'hospitalName',
        'categoryId',
        'ownershipId',
        'regionId',
        'zoneId',
        'woredaId',
        'physicalAddress',
        'primaryPhone',
        'contactPerson',
        'bankName',
        'bankAccountNumber',
        'accountHolderName'
      ];

      for (const field of requiredFields) {
        if (!this.hospitalForm[field]) {
          this.showAlert('failed', `Please fill in all required fields. Missing: ${this.formatFieldName(field)}`);
          return false;
        }
      }

      // Validate status reason if status is inactive or suspended
      if ((this.hospitalForm.status === 'inactive' || this.hospitalForm.status === 'suspended') && !this.hospitalForm.statusReason) {
        this.showAlert('failed', `Please provide a reason for ${this.hospitalForm.status === 'suspended' ? 'suspension' : 'inactivation'}`);
        return false;
      }

      // Validate email format if provided
      if (this.hospitalForm.hospitalEmail && !this.isValidEmail(this.hospitalForm.hospitalEmail)) {
        this.showAlert('failed', 'Please enter a valid email address');
        return false;
      }

      // Validate phone number format
      if (!this.isValidPhone(this.hospitalForm.primaryPhone)) {
        this.showAlert('failed', 'Please enter a valid primary phone number');
        return false;
      }

      return true;
    },

    formatFieldName(fieldName) {
      return fieldName
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    isValidPhone(phone) {
      // Ethiopian phone number format
      const phoneRegex = /^(\+251|0)?[0-9]{9}$/;
      return phoneRegex.test(phone.replace(/\s/g, ''));
    },

    async updateHospital() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;
      this.clearAlerts();

      try {
        // Prepare the data for the API call
        const data = {
          function: "updateHospital",
          hospitalId: this.hospitalId,
          hospitalName: this.hospitalForm.hospitalName,
          categoryId: this.hospitalForm.categoryId,
          ownershipId: this.hospitalForm.ownershipId,
          bedCapacity: this.hospitalForm.bedCapacity,
          regionId: this.hospitalForm.regionId,
          zoneId: this.hospitalForm.zoneId,
          woredaId: this.hospitalForm.woredaId,
          kebeleId: this.hospitalForm.kebeleId,
          physicalAddress: this.hospitalForm.physicalAddress,
          postalAddress: this.hospitalForm.postalAddress,
          postalCode: this.hospitalForm.postalCode,
          poBox: this.hospitalForm.poBox,
          latitude: this.hospitalForm.latitude,
          longitude: this.hospitalForm.longitude,
          primaryPhone: this.hospitalForm.primaryPhone,
          secondaryPhone: this.hospitalForm.secondaryPhone,
          hospitalEmail: this.hospitalForm.hospitalEmail,
          website: this.hospitalForm.website,
          contactPerson: this.hospitalForm.contactPerson,
          contactPersonPosition: this.hospitalForm.contactPersonPosition,
          bankName: this.hospitalForm.bankName,
          bankAccountNumber: this.hospitalForm.bankAccountNumber,
          accountHolderName: this.hospitalForm.accountHolderName,
          bankBranchName: this.hospitalForm.bankBranchName,
          bankBranchCode: this.hospitalForm.bankBranchCode,
          swiftBicCode: this.hospitalForm.swiftBicCode,
          operatingHours: this.hospitalForm.operatingHours,
          specialNotes: this.hospitalForm.specialNotes,
          emergencyServices: this.hospitalForm.emergencyServices,
          referralCenter: this.hospitalForm.referralCenter,
          ambulanceAvailable: this.hospitalForm.ambulanceAvailable,
          services: this.hospitalForm.services,
          status: this.hospitalForm.status,
          statusReason: this.hospitalForm.statusReason
        };

        // Make the actual API call
        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.showAlert('success', response.data.message || 'Hospital information updated successfully');

          // Clear the stored hospital data from sessionStorage
          sessionStorage.removeItem('editHospital');

          // Redirect to hospitals list after 2 seconds
          setTimeout(() => {
            router.push('/hospitals');
          }, 2000);
        } else {
          this.showAlert('failed', response.data.message || 'Failed to update hospital information');
        }

      } catch (error) {
        console.error('Error updating hospital:', error);
        this.showAlert('failed', error.message || 'An error occurred while updating hospital information');
      } finally {
        this.loading = false;
      }
    },

    detectChanges() {
      this.changes = [];

      const fieldMapping = {
        hospitalName: { label: 'Hospital Name', original: 'hospital_name' },
        categoryId: { label: 'Category', original: 'category_id' },
        ownershipId: { label: 'Ownership Type', original: 'ownership_id' },
        bedCapacity: { label: 'Bed Capacity', original: 'bed_capacity' },
        regionId: { label: 'Region', original: 'region_id' },
        zoneId: { label: 'Zone', original: 'zone_id' },
        woredaId: { label: 'Woreda', original: 'woreda_id' },
        kebeleId: { label: 'Kebele', original: 'kebele_id' },
        physicalAddress: { label: 'Physical Address', original: 'physical_address' },
        postalAddress: { label: 'Postal Address', original: 'postal_address' },
        postalCode: { label: 'Postal Code', original: 'postal_code' },
        poBox: { label: 'P.O. Box', original: 'po_box' },
        latitude: { label: 'Latitude', original: 'latitude' },
        longitude: { label: 'Longitude', original: 'longitude' },
        primaryPhone: { label: 'Primary Phone', original: 'primary_phone' },
        secondaryPhone: { label: 'Secondary Phone', original: 'secondary_phone' },
        hospitalEmail: { label: 'Email', original: 'hospital_email' },
        website: { label: 'Website', original: 'website' },
        contactPerson: { label: 'Contact Person', original: 'contact_person' },
        contactPersonPosition: { label: 'Contact Position', original: 'contact_person_position' },
        bankName: { label: 'Bank Name', original: 'bank_name' },
        bankAccountNumber: { label: 'Bank Account', original: 'bank_account_number' },
        accountHolderName: { label: 'Account Holder', original: 'account_holder_name' },
        bankBranchName: { label: 'Bank Branch', original: 'bank_branch_name' },
        bankBranchCode: { label: 'Branch Code', original: 'bank_branch_code' },
        swiftBicCode: { label: 'SWIFT Code', original: 'swift_bic_code' },
        operatingHours: { label: 'Operating Hours', original: 'operating_hours' },
        emergencyServices: { label: 'Emergency Services', original: 'emergency_services' },
        referralCenter: { label: 'Referral Center', original: 'referral_center' },
        ambulanceAvailable: { label: 'Ambulance Available', original: 'ambulance_available' },
        status: { label: 'Status', original: 'status' },
        statusReason: { label: 'Status Reason', original: 'status_reason' }
      };

      for (const [field, mapping] of Object.entries(fieldMapping)) {
        const currentValue = this.hospitalForm[field];
        const originalField = mapping.original;
        let originalValue = this.originalHospitalData[originalField];

        // Handle boolean conversions
        if (field === 'emergencyServices' || field === 'referralCenter' || field === 'ambulanceAvailable') {
          originalValue = originalValue == 1;
        }

        // Handle statusReason (new field)
        if (field === 'statusReason') {
          originalValue = originalValue || '';
        }

        if (currentValue !== originalValue) {
          this.changes.push({
            field: field,
            label: mapping.label,
            original: originalValue,
            new: currentValue
          });
        }
      }
    },

    viewChanges() {
      this.detectChanges();
      const modal = new bootstrap.Modal(document.getElementById('changesModal'));
      modal.show();
    },

    cancelEdit() {
      if (this.changes.length > 0) {
        if (confirm('You have unsaved changes. Are you sure you want to cancel?')) {
          router.push('/hospitals');
        }
      } else {
        router.push('/hospitals');
      }
    }
  },

  watch: {
    hospitalForm: {
      handler() {
        this.detectChanges();
        // Clear status reason when status becomes active
        if (this.hospitalForm.status === 'active') {
          this.hospitalForm.statusReason = '';
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.alert {
  margin-bottom: 1rem;
}

.form-check-input:checked {
  background-color: #5156be;
  border-color: #5156be;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-header {
  background-color: #f8f9fa;
}

.table-bordered th {
  background-color: #f8f9fa;
  font-weight: 600;
}
</style>